/**
 * Shoppex Storefront SDK
 *
 * Usage:
 * ```html
 * <script src="https://cdn.shoppex.io/sdk/v1.0/shoppex.umd.js"></script>
 * <script>
 *   shoppex.init('my-store');
 *
 *   shoppex.getStore().then(store => console.log(store));
 *   shoppex.addToCart('product-id', 'variant-id', 2);
 *   shoppex.checkout();
 * </script>
 * ```
 */

import { initConfig, isInitialized, getConfig } from './core/config';
import { getTypedClient } from './core/typed-client';
import type { ShoppexInitOptions } from './types';

// Re-export types
export * from './types';
export * from './core/errors';
export {
  buildStorefrontCustomFieldPayload,
  isStorefrontCheckboxCustomFieldValueChecked,
  normalizeStorefrontCustomFields,
  validateStorefrontCustomFieldValue,
} from './utils/storefront-custom-fields';
export type { StorefrontCustomField } from './utils/storefront-custom-fields';
export {
  isProductInStock,
  isProductOutOfStock,
  isVariantOutOfStock,
  resolveDisplayStock,
  resolveVariantStockValue,
} from './utils/storefront-stock';
export {
  buildStorefrontProductLookup,
  getMergedStorefrontProducts,
  getStorefrontGroupProducts,
} from './utils/storefront-catalog';
export {
  stripHtmlFromText,
  normalizeSearchQuery,
  collectProductSearchHaystack,
  productMatchesSearchQuery,
  groupMatchesSearchQuery,
  filterProductsBySearchQuery,
  searchMergedStorefrontCatalog,
  searchMergedStorefrontCatalogItems,
} from './utils/storefront-search';
export type { StorefrontSearchFilterOptions, StorefrontCatalogSearchItem } from './utils/storefront-search';
export {
  buildStorefrontContactMessage,
  resolveStorefrontApiBaseUrl,
  resolveStorefrontSocialLinks,
  submitStorefrontContactTicket,
} from './utils/storefront-contact';
export type {
  StorefrontContactTicketInput,
  StorefrontContactTicketResult,
  StorefrontSocialLinks,
} from './utils/storefront-contact';
export type {
  BlockDefinition,
  BlockInstance,
  PageLayout,
  ThemeConfig,
  ThemeBlockManifest,
  SettingField,
  SectionDefinition,
  ResolvedThemeSettings,
} from './types/theme-config';
export type { PublishedBuilderSettings, PublishedThemeSettingsPayload } from './modules/theme';
export type { NavigationMenuSlot } from '@shoppex/contracts/navigation';
export {
  CATALOG_UNIT_PRICE_DECIMAL_PLACES,
  CATALOG_UNIT_PRICE_FORMAT_OPTIONS,
  PAYABLE_AMOUNT_DECIMAL_PLACES,
  roundPayableAmount,
} from '@shoppex/contracts/catalog-unit-price';

// Store module
import {
  getStore,
  getStorefront,
  getStoreLogoUrl,
  getStoreBannerUrl,
  resolveStoreByDomain,
} from './modules/store';

// Products module
import { getProducts, getProduct, getCategories, getStorefrontProductsPage } from './modules/products';

// Cart module
import {
  getCart,
  getCartItemCount,
  getCartCoupon,
  getCartCouponSource,
  setCartCoupon,
  clearCartCoupon,
  addToCart,
  setCartItem,
  updateCartItem,
  removeFromCart,
  clearCart,
  createCartBackup,
  restoreCartFromBackup,
  mergeBaskets,
  moveBasketItem,
  getCartStats,
  validateCartIntegrity,
  quoteCart,
  getLatestQuoteToken,
  resolveCartLineId,
} from './modules/cart';
// resolveCartLineId is re-exported on the default shoppex object below (not only type-only).
export { computeCartLineId, ensureCartLineId } from './utils/cart-line-id';
export type { CartLineIdentityInput } from './utils/cart-line-id';

// Checkout module
import { checkout, buildCheckoutUrl, buildCheckoutUrlSync, getRequestedCheckoutCurrency } from './modules/checkout';
export type { CheckoutOptions, CheckoutResult } from './modules/checkout';
export { CheckoutCreateError } from './modules/checkout';
export type { SearchOptions } from './modules/search';

// Affiliate module
import {
  applyAffiliateCode,
  captureAffiliateFromUrl,
  getAffiliateCode,
  getStoredAffiliate,
  setAffiliateCode,
  clearAffiliateCode,
  trackAffiliateEvent,
  validateAffiliateCode,
} from './modules/affiliates';

// Coupons module
import { validateCoupon } from './modules/coupons';

// Reviews module
import { getShopReviews, getShopReviewsPage } from './modules/reviews';
export { getShopReviewsPage } from './modules/reviews';

// Customer module
import {
  requestOtp,
  verifyOtp,
  logout,
  me,
  dashboard,
  orders,
  order,
  loyalty,
  redeemLoyaltyPoints,
  warranties,
  claimWarranty,
  resetLicenseHwid,
  subscriptionBillingHistory,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  favorites,
  addFavorite,
  removeFavorite,
  affiliate,
  affiliateStats,
  createTicket,
  ticket,
  replyToTicket,
  updateProfile,
  updateAvatar,
  removeAvatar,
  emailPreferences,
  updateEmailPreferences,
  sessions,
  revokeSession,
  revokeAllSessions,
  reseller,
  applyForReseller,
  enrollAsReseller,
  acceptResellerInvite,
  resellerCatalog,
  quoteResellerOrder,
  resellerOrders,
  resellerOrder,
  resellerWallet,
  resellerApiKeys,
} from './modules/customer';
export {
  requestOtp,
  verifyOtp,
  logout,
  me,
  dashboard,
  orders,
  order,
  loyalty,
  redeemLoyaltyPoints,
  warranties,
  claimWarranty,
  resetLicenseHwid,
  subscriptionBillingHistory,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  favorites,
  addFavorite,
  removeFavorite,
  affiliate,
  affiliateStats,
  createTicket,
  ticket,
  replyToTicket,
  updateProfile,
  updateAvatar,
  removeAvatar,
  emailPreferences,
  updateEmailPreferences,
  sessions,
  revokeSession,
  revokeAllSessions,
  reseller,
  applyForReseller,
  enrollAsReseller,
  acceptResellerInvite,
  resellerCatalog,
  quoteResellerOrder,
  resellerOrders,
  resellerOrder,
  resellerWallet,
  resellerApiKeys,
} from './modules/customer';
export type {
  CustomerTicketPayload,
  CustomerProfilePatch,
  CustomerSubscriptionCancelOptions,
  CustomerEmailPreferencesPatch,
  ResellerOrderItem,
  ResellerCatalogQuery,
  ResellerOrdersQuery,
} from './modules/customer';

// Search module
import { searchProducts, searchCatalogItems } from './modules/search';

// Invoices module
import { getInvoice, getInvoiceStatus } from './modules/invoices';

// Pages module
import { getPages, getPage } from './modules/pages';

// Navigation module
import { getMenus, getMenu, getMenuBySlot, getMenuByTitle, getMenuSlotTitles } from './modules/navigation';

// Analytics module
import { trackPageView } from './modules/analytics';
import { getStorefrontOnlineUsers, getStorefrontRecentSales, touchStorefrontPresence } from './modules/presence';

// Format utilities
import { createFormatter, formatPrice } from './utils/format';
import { clearCache, invalidateCache, getCacheStats } from './core/cache';
import { fetchPublishedBuilderSettings, fetchPublishedThemeSettings, resolveDefaults, mergeSettings } from './modules/theme';

export { fetchPublishedBuilderSettings, fetchPublishedThemeSettings, resolveDefaults, mergeSettings } from './modules/theme';
export { trackPageView } from './modules/analytics';
export { getStorefrontOnlineUsers, getStorefrontRecentSales, touchStorefrontPresence } from './modules/presence';
export { getMenus, getMenu, getMenuBySlot, getMenuByTitle, getMenuSlotTitles } from './modules/navigation';

/**
 * Initialize the SDK with a store slug
 */
function init(storeSlug: string, options?: ShoppexInitOptions): void;
function init(options: ShoppexInitOptions & { storeId: string }): void;
function init(
  storeSlugOrOptions: string | (ShoppexInitOptions & { storeId: string }),
  options?: ShoppexInitOptions
): void {
  if (typeof storeSlugOrOptions === 'string') {
    initConfig(storeSlugOrOptions, options);
  } else {
    initConfig(storeSlugOrOptions.storeId, storeSlugOrOptions);
  }
}

/**
 * Shoppex SDK instance
 */
export const shoppex = {
  // Initialization
  init,
  isInitialized,
  getConfig,

  // Typed OpenAPI client — drop to this when you need an endpoint the
  // high-level modules below do not cover yet.
  client: getTypedClient,

  // Store
  getStore,
  getStorefront,
  getStoreLogoUrl,
  getStoreBannerUrl,
  resolveStoreByDomain,

  // Products
  getProducts,
  getStorefrontProductsPage,
  getProduct,
  getCategories,

  // Cart
  getCart,
  getCartItemCount,
  getCartCoupon,
  getCartCouponSource,
  setCartCoupon,
  clearCartCoupon,
  addToCart,
  setCartItem,
  updateCartItem,
  removeFromCart,
  clearCart,
  createCartBackup,
  restoreCartFromBackup,
  mergeBaskets,
  moveBasketItem,
  getCartStats,
  validateCartIntegrity,
  quoteCart,
  getLatestQuoteToken,
  resolveCartLineId,

  // Checkout
  checkout,
  buildCheckoutUrl,
  buildCheckoutUrlSync,
  getRequestedCheckoutCurrency,

  // Affiliates
  captureAffiliateFromUrl,
  validateAffiliateCode,
  applyAffiliateCode,
  getAffiliateCode,
  getStoredAffiliate,
  setAffiliateCode,
  clearAffiliateCode,
  trackAffiliateEvent,

  // Coupons
  validateCoupon,

  // Reviews
  getShopReviews,
  getShopReviewsPage,

  // Customer account
  requestOtp,
  verifyOtp,
  logout,
  me,
  dashboard,
  orders,
  order,
  loyalty,
  redeemLoyaltyPoints,
  warranties,
  claimWarranty,
  resetLicenseHwid,
  subscriptionBillingHistory,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  favorites,
  addFavorite,
  removeFavorite,
  affiliate,
  affiliateStats,
  createTicket,
  ticket,
  replyToTicket,
  updateProfile,
  updateAvatar,
  removeAvatar,
  emailPreferences,
  updateEmailPreferences,
  sessions,
  revokeSession,
  revokeAllSessions,
  reseller,
  applyForReseller,
  enrollAsReseller,
  acceptResellerInvite,
  resellerCatalog,
  quoteResellerOrder,
  resellerOrders,
  resellerOrder,
  resellerWallet,
  resellerApiKeys,

  // Search
  searchProducts,
  searchCatalogItems,

  // Invoices
  getInvoice,
  getInvoiceStatus,

  // Pages
  getPages,
  getPage,

  // Navigation
  getMenus,
  getMenu,
  getMenuBySlot,
  getMenuByTitle,
  getMenuSlotTitles,

  // Analytics
  trackPageView,
  getStorefrontOnlineUsers,
  getStorefrontRecentSales,
  touchStorefrontPresence,

  // Formatting
  createFormatter,
  formatPrice,

  // Cache
  clearCache,
  invalidateCache,
  getCacheStats,

  // Theme settings helpers
  fetchPublishedBuilderSettings,
  fetchPublishedThemeSettings,
  resolveDefaults,
  mergeSettings,
};

// Default export for ES modules
export default shoppex;

// UMD global exposure — never clobber an already-initialized client. Theme
// artifacts can load multiple SDK chunks; a late bundle must not reset init.
if (typeof window !== 'undefined') {
  const w = window as unknown as { shoppex?: typeof shoppex };
  if (!w.shoppex?.isInitialized?.()) {
    w.shoppex = shoppex;
  }
}
